import React, { useState, useCallback, useEffect } from 'react';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonTable from 'components/commonTable/CommonTable';
import CommonPagination from 'components/commonPagination/CommonPagination';
import ActivityLogFilter from './ActivityLogFilter';
import { useDispatch, useSelector } from 'react-redux';
import { ListActivityLogData } from 'reactRedux/thunks/adminUserThunk/adminUserThunk';
import * as dateFns from 'date-fns';

const ActivityLogList = () => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const dispatch = useDispatch();
  const addDays = dateFns.addDays;

  // State management
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [debouncedLocationSearch, setDebouncedLocationSearch] = useState('');
  const [dateRange, setDateRange] = useState([
    {
      endDate: new Date(),
      startDate: addDays(new Date(), -6), // Default 7 days range
      key: 'selection',
    },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [calendarFilterTitle, setCalendarFilterTitle] = useState('Last 7 days');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 7 days');

  // Redux state
  const { isLoading, activityLogData } = useSelector(
    (state) => state.AdminUserSlice,
  );

  // Fetch activity log data
  const getActivityLogList = useCallback(
    (adminName, location, currentDateRange = dateRange) => {
      const startDate = dateFns.format(
        currentDateRange[0]?.startDate,
        'yyyy-MM-dd',
      );
      const endDate = dateFns.format(
        currentDateRange[0]?.endDate,
        'yyyy-MM-dd',
      );
      dispatch(
        ListActivityLogData({
          page: page,
          limit: 10,
          adminName: adminName,
          locationCity: location,
          startDate: startDate,
          endDate: endDate,
        }),
      );
    },
    [dispatch, page, dateRange],
  );

  // Debounced search effects
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedLocationSearch(locationSearch);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [locationSearch]);

  // Main effect to fetch data
  useEffect(() => {
    getActivityLogList(debouncedSearch, debouncedLocationSearch, dateRange);
  }, [
    debouncedSearch,
    debouncedLocationSearch,
    page,
    dateRange,
    getActivityLogList,
  ]);

  // Date range handlers
  const handleApply = () => {
    setDateRange(tempDateRange);
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setShowCalendar(false);
    setPage(1);
  };

  const handleClear = () => {
    const defaultRange = [
      {
        endDate: new Date(),
        startDate: addDays(new Date(), -6),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setTempCalendarFilterTitle('Last 7 days');
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.adminName, {
      id: 'adminName',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Admin Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.loginTime, {
      id: 'loginTime',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Login Date/Time
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue()
            ? dateFns.format(new Date(info.getValue()), 'dd/MM/yyyy HH:mm')
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.duration, {
      id: 'duration',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Session Duration
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue() || 'Active'}
        </Text>
      ),
    }),
    columnHelper.accessor(
      (row) => `${row?.locationCity || 'N/A'} (${row?.ip || 'N/A'})`,
      {
        id: 'location',
        header: () => (
          <Text
            align="left"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
            whiteSpace="nowrap"
          >
            Location/IP Address
          </Text>
        ),
        cell: (info) => (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            {info.getValue()}
          </Text>
        ),
      },
    ),
  ];

  return (
    <Box>
      <ActivityLogFilter
        search={search}
        setSearch={setSearch}
        locationSearch={locationSearch}
        setLocationSearch={setLocationSearch}
        setPage={setPage}
        showCalendar={showCalendar}
        setShowCalendar={setShowCalendar}
        tempDateRange={tempDateRange}
        setTempDateRange={setTempDateRange}
        calendarFilterTitle={calendarFilterTitle}
        setCalendarFilterTitle={setCalendarFilterTitle}
        setTempCalendarFilterTitle={setTempCalendarFilterTitle}
        handleApply={handleApply}
        handleClear={handleClear}
        setDateRange={setDateRange}
        setDebouncedSearch={setDebouncedSearch}
        setDebouncedLocationSearch={setDebouncedLocationSearch}
      />

      <CommonTable
        columns={columns}
        data={activityLogData?.list || []}
        isLoading={isLoading}
        keyValue="id"
        dynamicWidth={{
          adminName: '200px',
          loginTime: '200px',
          duration: '150px',
          location: '250px',
        }}
      />

      <CommonPagination
        totalCount={activityLogData?.total || 0}
        activePage={page}
        pageCount={Math.ceil(
          (activityLogData?.total || 0) / (activityLogData?.limit || 10),
        )}
        onPageChange={(e) => {
          setPage(e);
        }}
        itemsPerPage={activityLogData?.limit || 10}
      />
    </Box>
  );
};

export default ActivityLogList;
